/* eslint-disable react/no-unknown-property */
import React, { useEffect, useRef, useState } from 'react';
import {
  View, Text, Alert, ActivityIndicator, TextInput,
  TouchableOpacity, Modal, StyleSheet, Platform
} from 'react-native';
import { API_BASE } from '../utils';
import { useIsFocused } from '@react-navigation/native';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO } from '../components/padiproTheme';

// Haptics (native only)
let Haptics = null;
if (Platform.OS !== 'web') {
  try { Haptics = require('expo-haptics'); } catch { Haptics = null; }
}

// Native scanner (native only)
let BarCodeScanner = null;
if (Platform.OS !== 'web') {
  try { BarCodeScanner = require('expo-barcode-scanner').BarCodeScanner; } catch { BarCodeScanner = null; }
}

export default function ScannerScreen({ route }) {
  const user = route?.params?.user || null;

  // Shared
  const [loading, setLoading] = useState(false);
  const [pesticide, setPesticide] = useState(null);
  const [qty, setQty] = useState('1');
  const [qtyModal, setQtyModal] = useState(false);

  // Success popup
  const [successModal, setSuccessModal] = useState({
    visible: false, title: '', message: '', timer: null,
  });

  // Native
  const [hasPermission, setHasPermission] = useState(Platform.OS === 'web' ? true : null);
  const [scanning, setScanning] = useState(true);

  // Web
  const isFocused = useIsFocused();
  const html5QrcodeRef = useRef(null);
  const webScanRunningRef = useRef(false);
  const [webReady, setWebReady] = useState(false);
  const [webError, setWebError] = useState('');
  const lastDecodedRef = useRef('');        // hindari duplikat cepat
  const lastDecodedAtRef = useRef(0);

  // --- logging kecil ke backend (ringan & timeout pendek) ---
  async function fastFetch(url, { method='GET', headers={}, body=null, timeoutMs=1200 } = {}) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      return await fetch(url, { method, headers, body, signal: ctrl.signal });
    } finally { clearTimeout(t); }
  }
  const logScan = async (payload) => {
    try {
      await fastFetch(`${API_BASE}/scan-logs`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ platform: Platform.OS, ...payload }),
        timeoutMs: 1000,
      });
    } catch {}
  };

  const vibrateLight = async () => {
    if (Haptics?.impactAsync) {
      try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {}
    }
  };

  const fetchByQr = async (code) => {
    const r = await fastFetch(`${API_BASE}/pesticides/byqr/${encodeURIComponent(code)}`, { timeoutMs: 2500 });
    if (!r.ok) {
      let msg = 'QR tidak dikenal';
      try { const j = await r.json(); if (j?.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    return r.json();
  };

  const submitUseAPI = async ({ qr_code, qtyValue }) => {
    const resp = await fastFetch(`${API_BASE}/transactions/use`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qr_code, user_id: user?.user_id || null, qty: qtyValue }),
      timeoutMs: 3000
    });
    const json = await resp.json();
    if (!resp.ok) throw new Error(json?.error || 'Gagal submit');
    return json;
  };

  const onQrRead = async (decodedText) => {
    const now = Date.now();
    // filter duplikat <1.2s (kamera bisa spam callback)
    if (decodedText === lastDecodedRef.current && now - lastDecodedAtRef.current < 1200) {
      return;
    }
    lastDecodedRef.current = decodedText;
    lastDecodedAtRef.current = now;

    setLoading(true);
    await logScan({ stage:'decoded', success:true, code:decodedText });
    try {
      const p = await fetchByQr(decodedText);
      await logScan({ stage:'lookup_ok', success:true, code:decodedText });
      await vibrateLight();
      setPesticide(p);
      setQty('1');
      setQtyModal(true);
    } catch (e) {
      await logScan({ stage:'lookup_fail', success:false, code:decodedText, error: e?.message || 'lookup error' });
      Alert.alert('Gagal scan', e?.message || 'QR tidak valid');
      // siap scan lagi
      if (Platform.OS === 'web') {
        webScanRunningRef.current = true;
        await startWebScannerSafe();
      } else {
        setScanning(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitUse = async () => {
    const q = parseInt(qty, 10);
    if (!Number.isFinite(q) || q <= 0) {
      Alert.alert('Jumlah salah', 'Masukkan angka > 0');
      return;
    }
    if (pesticide?.stock != null && q > pesticide.stock) {
      Alert.alert('Stok tidak cukup', `Stok tersedia ${pesticide.stock}.`);
      return;
    }

    setLoading(true);
    try {
      const res = await submitUseAPI({ qr_code: pesticide.qr_code, qtyValue: q });

      // Tutup form qty, reset state item
      setQtyModal(false);
      setPesticide(null);
      setQty('1');

      await logScan({ stage:'submit_ok', success:true, code:res?.pesticide?.qr_code });

      const sisa = res?.pesticide?.stock ?? '—';
      showSuccess({ title: 'Input Tersimpan', message: `${res.message || 'Penggunaan tercatat'}\nSisa stok: ${sisa}` });
    } catch (e) {
      await logScan({ stage:'submit_fail', success:false, code:pesticide?.qr_code, error:e?.message||'submit error' });
      Alert.alert('Gagal', e?.message || 'Terjadi kesalahan');
      // Ready to rescan setelah error
      if (Platform.OS === 'web') {
        webScanRunningRef.current = true;
        await startWebScannerSafe();
      } else {
        setScanning(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // success popup
  const showSuccess = ({ title, message }) => {
    if (successModal.timer) clearTimeout(successModal.timer);
    const timer = setTimeout(() => { hideSuccess(); }, 1200);
    setSuccessModal({ visible: true, title, message, timer });
  };
  const hideSuccess = async () => {
    if (successModal.timer) clearTimeout(successModal.timer);
    setSuccessModal({ visible: false, title: '', message: '', timer: null });
    if (Platform.OS === 'web') {
      webScanRunningRef.current = true;
      await startWebScannerSafe();
    } else {
      setScanning(true);
    }
  };

  /** ======= NATIVE: permission ======= */
  useEffect(() => {
    if (Platform.OS === 'web') return;
    let mounted = true;
    (async () => {
      if (!BarCodeScanner?.requestPermissionsAsync) { setHasPermission(false); return; }
      const { status } = await BarCodeScanner.requestPermissionsAsync();
      if (mounted) setHasPermission(status === 'granted');
      if (status === 'granted') await logScan({ stage:'start', success:true });
    })();
    return () => { mounted = false; };
  }, []);

  /** ======= WEB: start/stop html5-qrcode dengan tuning ======= */
  async function stopWebScannerSafe() {
    if (Platform.OS !== 'web') return;
    const s = html5QrcodeRef.current;
    if (!s) return;
    try {
      const state = s.getState?.(); // 1: SCANNING, 2: PAUSED
      if (state === 1 || state === 2) { await s.stop(); }
    } catch {}
    try { await s.clear(); } catch {}
    html5QrcodeRef.current = null;
    webScanRunningRef.current = false;
  }

  async function startWebScannerSafe() {
    if (Platform.OS !== 'web') return;

    // HTTPS-only (kecuali localhost)
    if (typeof window !== 'undefined') {
      const secure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      if (!secure) {
        setWebError('Kamera hanya bisa diakses via HTTPS atau localhost.');
        return;
      }
    }

    if (html5QrcodeRef.current && webScanRunningRef.current) return;

    try {
      const { Html5Qrcode } = await import('html5-qrcode');

      const elementId = 'web-qr-scanner';
      let container = null;
      for (let i = 0; i < 50; i += 1) {
        container = document.getElementById(elementId);
        if (container) {
          const rect = container.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) break;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 80));
      }
      if (!container) {
        setWebError('Container scanner tidak ditemukan.');
        return;
      }

      html5QrcodeRef.current = new Html5Qrcode(elementId, /* verbose */ false);

      await html5QrcodeRef.current.start(
        { facingMode: { exact: 'environment' } },
        {
          fps: 15,                 // naikkan FPS → cepat lock
          qrbox: (vw, vh) => {
            const edge = Math.min(vw, vh);
            const box = Math.floor(edge * 0.70);  // kotak lebih besar
            return { width: box, height: box };
          },
          disableFlip: true,       // hemat komputasi
          rememberLastUsedCamera: true,
          // showTorchButton: true, // bisa diaktifkan kalau mau
          // showZoomSlider: true   // bisa diaktifkan kalau perlu
        },
        async (decodedText) => {
          if (!webScanRunningRef.current) return;
          webScanRunningRef.current = false;         // hentikan callback ganda
          await stopWebScannerSafe();                // stop cepat
          await onQrRead(decodedText);
        },
        /* onScanError */ () => {}
      );

      webScanRunningRef.current = true;
      setWebReady(true);
      setWebError('');
      await logScan({ stage:'start', success:true });
    } catch (e) {
      setWebError(e?.message || 'Gagal memulai kamera web.');
      await logScan({ stage:'start', success:false, error: e?.message || 'start fail' });
      await stopWebScannerSafe();
    }
  }

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    let disposed = false;
    (async () => {
      if (isFocused) {
        await startWebScannerSafe();
      } else {
        disposed = true;
        await stopWebScannerSafe();
      }
    })();
    return () => {
      disposed = true;
      stopWebScannerSafe();
    };
  }, [isFocused]);

  /** ========== UI ========== */

  if (Platform.OS !== 'web') {
    if (hasPermission === null) {
      return (
        <View style={s.center}>
          <ActivityIndicator />
          <Text style={{ marginTop: 8 }}>Meminta izin kamera...</Text>
        </View>
      );
    }
    if (hasPermission === false) {
      return (
        <View style={s.center}>
          <Text style={{ marginBottom: 8 }}>Izin kamera ditolak.</Text>
          <Text>Buka Settings dan izinkan kamera untuk aplikasi ini.</Text>
        </View>
      );
    }
  }

  return (
    <View style={s.screen}>
      <EnterpriseHeader
        title="Scanner PanenKu"
        subtitle="Arahkan kamera ke label produk pada kemasan"
      />

      {/* Scanner Viewport */}
      <View style={s.scanWrap}>
        {Platform.OS === 'web' ? (
          <div id="web-qr-scanner" style={s.scanner} />
        ) : (
          BarCodeScanner ? (
            <BarCodeScanner
              onBarCodeScanned={scanning ? ({ data }) => {
                setScanning(false);
                onQrRead(data);
              } : undefined}
              style={s.scanner}
            />
          ) : (
            <View style={[s.scanner, s.center]}>
              <Text style={{ color: '#fff' }}>Modul scanner native tidak tersedia.</Text>
            </View>
          )
        )}

        {/* Frame kotak */}
        <View pointerEvents="none" style={s.frame}>
          <View style={s.cornerTL} />
          <View style={s.cornerTR} />
          <View style={s.cornerBL} />
          <View style={s.cornerBR} />
        </View>

        {Platform.OS === 'web' && !webReady && !webError ? (
          <View style={s.loadingOverlay}>
            <ActivityIndicator color="#fff" />
            <Text style={{ color: '#fff', marginTop: 6 }}>Menyiapkan kamera...</Text>
          </View>
        ) : null}

        {Platform.OS === 'web' && !!webError && (
          <View style={s.loadingOverlay}>
            <Text style={{ color: '#fff', textAlign: 'center' }}>{webError}</Text>
          </View>
        )}
      </View>

      {/* Footer controls */}
      <View style={{ paddingTop: 12, alignItems: 'center' }}>
        <TouchableOpacity
          onPress={async () => {
            if (Platform.OS === 'web') {
              await stopWebScannerSafe();
              webScanRunningRef.current = true;
              await startWebScannerSafe();
            } else {
              setScanning(true);
            }
          }}
          style={s.btnGhost}
          accessibilityRole="button"
          accessibilityLabel="Scan lagi"
        >
          <Text style={{ color: '#fff', fontWeight: '600' }}>Scan Lagi</Text>
        </TouchableOpacity>
      </View>

      {/* Loading overlay global */}
      {loading && (
        <View style={s.globalLoading}>
          <ActivityIndicator color="#fff" />
        </View>
      )}

      {/* Qty Modal */}
      <Modal
        visible={qtyModal}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setQtyModal(false);
          if (Platform.OS === 'web') startWebScannerSafe(); else setScanning(true);
        }}
      >
        <View style={s.modalBackdrop}>
          <View style={s.modalCard}>
            <Text style={s.title}>{pesticide?.name}</Text>
            <Text style={s.sub}>QR: {pesticide?.qr_code}</Text>
            <Text style={s.sub}>Stok saat ini: {pesticide?.stock} {pesticide?.unit}</Text>

            <Text style={{ marginTop: 12, fontWeight: '600' }}>Jumlah pakai</Text>
            <TextInput
              value={qty}
              onChangeText={setQty}
              keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
              style={s.input}
              placeholder="Masukkan jumlah"
            />

            <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
              <TouchableOpacity style={s.btnPrimary} onPress={submitUse}>
                <Text style={s.btnPrimaryText}>Submit</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={s.btnSecondary}
                onPress={() => {
                  setQtyModal(false);
                  if (Platform.OS === 'web') startWebScannerSafe(); else setScanning(true);
                }}
              >
                <Text style={s.btnSecondaryText}>Batal</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Success Popup */}
      <Modal visible={successModal.visible} transparent animationType="fade" onRequestClose={hideSuccess}>
        <View style={s.modalBackdropCenter}>
          <View style={s.successCard}>
            <View style={s.checkCircle}><Text style={s.checkText}>✓</Text></View>
            <Text style={s.successTitle}>{successModal.title}</Text>
            <Text style={s.successMsg}>{successModal.message}</Text>
            <TouchableOpacity style={[s.btnPrimary, { marginTop: 12 }]} onPress={hideSuccess}>
              <Text style={s.btnPrimaryText}>Selesai</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

/** ========= Styles ========= */
const s = StyleSheet.create({
  screen: { flex: 1, padding: 12, backgroundColor: PADIPRO.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  scanWrap: {
    flex: 1, borderRadius: 18, overflow: 'hidden',
    borderWidth: 1, borderColor: PADIPRO.border, marginTop: 10, backgroundColor: '#000',
    minHeight: 320,
  },
  scanner: { width: '100%', height: '100%', minHeight: 300 },
  frame: { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 },
  cornerTL: { position: 'absolute', left: 24, top: 24, width: 28, height: 28, borderLeftWidth: 3, borderTopWidth: 3, borderColor: PADIPRO.gold, borderRadius: 4 },
  cornerTR: { position: 'absolute', right: 24, top: 24, width: 28, height: 28, borderRightWidth: 3, borderTopWidth: 3, borderColor: PADIPRO.gold, borderRadius: 4 },
  cornerBL: { position: 'absolute', left: 24, bottom: 24, width: 28, height: 28, borderLeftWidth: 3, borderBottomWidth: 3, borderColor: PADIPRO.gold, borderRadius: 4 },
  cornerBR: { position: 'absolute', right: 24, bottom: 24, width: 28, height: 28, borderRightWidth: 3, borderBottomWidth: 3, borderColor: PADIPRO.gold, borderRadius: 4 },

  loadingOverlay: {
    position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,.35)'
  },
  globalLoading: {
    position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,.35)', alignItems: 'center', justifyContent: 'center'
  },

  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,.45)', alignItems: 'center', justifyContent: 'flex-end' },
  modalCard: { width: '100%', backgroundColor: PADIPRO.surface, padding: 16, borderTopLeftRadius: 16, borderTopRightRadius: 16 },
  title: { fontWeight: '700', fontSize: 16, color: PADIPRO.textStrong },
  sub: { color: PADIPRO.muted, marginTop: 4 },
  input: { borderWidth: 1, borderColor: PADIPRO.border, borderRadius: 10, padding: 10, marginTop: 6, minHeight: 44 },
  btnPrimary: { backgroundColor: PADIPRO.primary, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 10, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  btnPrimaryText: { color: '#fff', fontWeight: '700' },
  btnSecondary: { backgroundColor: PADIPRO.primarySoft, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 10, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  btnSecondaryText: { color: PADIPRO.text, fontWeight: '700' },
  btnGhost: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primary,
    minHeight: 44,
    justifyContent: 'center',
  },

  modalBackdropCenter: { flex: 1, backgroundColor: 'rgba(0,0,0,.45)', alignItems: 'center', justifyContent: 'center' },
  successCard: { width: '86%', backgroundColor: PADIPRO.surface, padding: 18, borderRadius: 16, alignItems: 'center' },
  checkCircle: { width: 64, height: 64, borderRadius: 32, backgroundColor: PADIPRO.primary, alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  checkText: { color: '#fff', fontSize: 32, fontWeight: '800', lineHeight: 34 },
  successTitle: { fontSize: 16, fontWeight: '800', marginTop: 2, textAlign: 'center', color: PADIPRO.textStrong },
  successMsg: { color: PADIPRO.muted, textAlign: 'center', marginTop: 6, lineHeight: 20 },
});
